// Angular
import { Injectable } from '@angular/core';

// Third party
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Account-history row identifier — null means no row expanded
 */
export type AccountHistoryRow =
  | 'statements'
  | 'betSummary'
  | 'overview'
  | null;

/**
 * Static descriptor used to render an account-history row
 */
export interface AccountHistoryRowDescriptor {
  id: Exclude<AccountHistoryRow, null>;
  label: string;
  iconLight: string;
  iconDark: string;
  dataCy: string;
  // Expandable rows reveal an inline panel; non-expandable rows are actions
  expandable: boolean;
  // True while the feature is not yet ported (shows a "coming soon" alert)
  comingSoon: boolean;
}

// Message shown for rows that are not yet available
export const ACCOUNT_HISTORY_COMING_SOON_MSG =
  "We're working hard to bring this feature to you! Stay tuned for updates.";

/**
 * State and pure helpers for the AccountHistory component
 */
@Injectable({ providedIn: 'root' })
export class AccountHistoryService {
  private readonly _activeRow$ = new BehaviorSubject<AccountHistoryRow>(null);

  readonly activeRow$: Observable<AccountHistoryRow> =
    this._activeRow$.asObservable();

  readonly rows: AccountHistoryRowDescriptor[] = [
    {
      id: 'statements',
      label: 'Statements',
      iconLight: '/assets/images/account_settings/account-statements-light.png',
      iconDark: '/assets/images/account_settings/account-statements-dark.png',
      dataCy: 'acc-history-statements',
      expandable: true,
      comingSoon: false
    },
    {
      id: 'betSummary',
      label: 'Bet Summary',
      iconLight: '/assets/images/account_settings/bet-summary-light.svg',
      iconDark: '/assets/images/account_settings/bet-summary-dark.svg',
      dataCy: 'acc-history-bet-sum',
      // Old: Bet Summary was a coming-soon action row
      // expandable: false,
      // comingSoon: true
      expandable: true,
      comingSoon: false
    },
    {
      id: 'overview',
      label: 'Overview',
      iconLight: '/assets/images/account_settings/overview-light.png',
      iconDark: '/assets/images/account_settings/overview-dark.png',
      dataCy: 'acc-history-overview',
      // Old: Overview was a coming-soon action row
      // expandable: false,
      // comingSoon: true
      expandable: true,
      comingSoon: false
    }
  ];

  get activeRow(): AccountHistoryRow {
    return this._activeRow$.getValue();
  }

  /**
   * Toggle a row open/closed (accordion behaviour)
   * @param {AccountHistoryRow} row Row identifier, or null to close all
   * @returns {void}
   */
  toggleRow(row: AccountHistoryRow): void {
    if (this._activeRow$.getValue() === row) {
      this._activeRow$.next(null);
      return;
    }
    this._activeRow$.next(row);
  }

  /**
   * Force-close every row
   * @returns {void}
   */
  closeAllRows(): void {
    this._activeRow$.next(null);
  }

  /**
   * Whether a row still shows the "coming soon" alert
   * @param {Exclude<AccountHistoryRow, null>} row Row identifier
   * @returns {boolean} True when the feature is not yet ported
   */
  isComingSoon(row: Exclude<AccountHistoryRow, null>): boolean {
    return this.rows.find((r) => r.id === row)?.comingSoon ?? false;
  }
}
